import { Button, Modal } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import ExportAssetsModal from "components/asset-management-system/ExportAssetsModal";
import style from "components/reports/erasure-reports-table.scss";
import { FilterOperator } from "domain/asset-management/assetFilterConstants";
import { Asset, AssetFilter, assetService, mapColumnsToApiPaths } from "services/asset-management/AssetService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    assets?: Asset[];
    columns: string[];
    totalAssetCount?: number;
    filters?: AssetFilter[];
    searchQuery?: string;
    selectedAssetIds?: string[];
    onExportDone?: () => void;
    customViewUuid?: string;
    sendEmail?: boolean;
    manifestExists?: boolean;
}

const ExportAssetsView = (props: Props) => {
    const { t } = useTranslation();
    const [processingModalVisible, setProcessingModalVisible] = React.useState(false);
    const [exportCount, setExportCount] = React.useState(0);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const exportAllAssets = props.selectedAssetIds === undefined;

    const handleExport = (includeManifestDetails: boolean, emailTo?: string, emailSubject?: string) => {
        setExportCount(exportAllAssets ? (props.totalAssetCount ?? 0) : (props.selectedAssetIds?.length ?? 0));

        setProcessingModalVisible(true);
        const resolvedFilters = exportAllAssets
            ? props.filters
            : [{ name: "asset_id", value: props.selectedAssetIds?.join(",") ?? "", operator: FilterOperator.IN }];
        const resolvedSearchQuery = exportAllAssets ? props.searchQuery : undefined;
        const paths = mapColumnsToApiPaths(props.columns);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const emailRequest =
            emailTo !== undefined && emailSubject !== undefined ? { to: emailTo, subject: emailSubject } : undefined;
        assetService
            .exportAssets(
                resolvedFilters,
                resolvedSearchQuery,
                paths,
                abortController,
                includeManifestDetails ? props.customViewUuid : undefined,
                props.sendEmail,
                emailRequest,
                props.customViewUuid
            )
            .catch((e) => {
                logger.error("Failed to export assets:", e);
            });
    };

    React.useEffect(() => {
        return () => abortControllers.forEach((abortController) => abortController.abort());
    }, []);

    return (
        <>
            <ExportAssetsModal
                isOpen={props.isOpen && !processingModalVisible}
                onClose={props.onClose}
                onExport={handleExport}
                sendEmail={props.sendEmail}
                manifestExists={props.manifestExists}
                allAssets={exportAllAssets}
                assetCount={props.selectedAssetIds?.length}
            />

            <Modal
                isOpen={processingModalVisible}
                hideModal={() => {
                    setProcessingModalVisible(false);
                    props.onExportDone?.();
                }}
                title={
                    exportAllAssets
                        ? t("AssetManagement.exportAllAssets")
                        : t("AssetManagement.exportAssets.title", { count: props.selectedAssetIds?.length })
                }
                buttons={
                    <Button
                        variant="Primary"
                        testId={testIds.common.dialog.closeButton}
                        onClick={() => {
                            setProcessingModalVisible(false);
                            props.onExportDone?.();
                        }}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <div className={classNames(style.processDialog, style.filters)}>
                    <div>
                        {props.sendEmail
                            ? t("AssetManagement.sendViaEmail.message", {
                                  count: exportCount,
                              })
                            : t("AssetManagement.exportAssets.message", {
                                  count: exportCount,
                              })}
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ExportAssetsView;
